import React, { useState } from 'react';
import { ChevronRightIcon, StarIcon, CheckCircleIcon, XCircleIcon, FileTextIcon, CoinsIcon } from 'lucide-react';
interface Client {
  id: string;
  ragioneSociale: string;
  piva: string;
  rating: number;
  indiceAffidabilita: number;
  importiScaduti: string;
  pagamentiScaduti: number;
  settore: string;
  ultimoPagamento: string;
  sincronizzato: boolean;
}
interface ClientsTableProps {
  searchTerm: string;
}
export function ClientsTable({
  searchTerm
}: ClientsTableProps) {
  // Dati fittizi per la tabella
  const clients: Client[] = [{
    id: '1',
    ragioneSociale: 'Acme Srl',
    piva: '12345678901',
    rating: 4,
    indiceAffidabilita: 85,
    importiScaduti: '€2.450',
    pagamentiScaduti: 2,
    settore: 'Servizi professionali',
    ultimoPagamento: '15/05/2023',
    sincronizzato: true
  }, {
    id: '2',
    ragioneSociale: 'TechSolutions Spa',
    piva: '98765432101',
    rating: 5,
    indiceAffidabilita: 95,
    importiScaduti: '€0',
    pagamentiScaduti: 0,
    settore: 'Tecnologia',
    ultimoPagamento: '28/05/2023',
    sincronizzato: true
  }, {
    id: '3',
    ragioneSociale: 'Industria Meccanica Srl',
    piva: '45678912301',
    rating: 3,
    indiceAffidabilita: 70,
    importiScaduti: '€8.750',
    pagamentiScaduti: 3,
    settore: 'Manifatturiero',
    ultimoPagamento: '03/04/2023',
    sincronizzato: true
  }, {
    id: '4',
    ragioneSociale: 'Logistica Express Srl',
    piva: '78901234561',
    rating: 2,
    indiceAffidabilita: 60,
    importiScaduti: '€12.300',
    pagamentiScaduti: 5,
    settore: 'Trasporti e logistica',
    ultimoPagamento: '10/02/2023',
    sincronizzato: false
  }, {
    id: '5',
    ragioneSociale: 'Alimentari Freschi Spa',
    piva: '56789012341',
    rating: 4,
    indiceAffidabilita: 80,
    importiScaduti: '€1.800',
    pagamentiScaduti: 1,
    settore: 'Agroalimentare',
    ultimoPagamento: '20/05/2023',
    sincronizzato: true
  }, {
    id: '6',
    ragioneSociale: 'Edilizia Moderna Srl',
    piva: '23456789011',
    rating: 3,
    indiceAffidabilita: 65,
    importiScaduti: '€6.500',
    pagamentiScaduti: 2,
    settore: 'Edilizia e costruzioni',
    ultimoPagamento: '05/04/2023',
    sincronizzato: true
  }, {
    id: '7',
    ragioneSociale: 'GreenEnergy Spa',
    piva: '89012345671',
    rating: 5,
    indiceAffidabilita: 90,
    importiScaduti: '€0',
    pagamentiScaduti: 0,
    settore: 'Energia e utilities',
    ultimoPagamento: '25/05/2023',
    sincronizzato: true
  }, {
    id: '8',
    ragioneSociale: 'Retail Fashion Srl',
    piva: '34567890121',
    rating: 2,
    indiceAffidabilita: 55,
    importiScaduti: '€15.200',
    pagamentiScaduti: 6,
    settore: 'Retail/GDO/e-commerce',
    ultimoPagamento: '15/01/2023',
    sincronizzato: false
  }];
  // Filtraggio per termine di ricerca
  const filteredClients = clients.filter(client => client.ragioneSociale.toLowerCase().includes(searchTerm.toLowerCase()) || client.piva.includes(searchTerm));
  // Funzione per renderizzare le stelle del rating
  const renderRating = (rating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(<StarIcon key={i} className={`h-4 w-4 ${i <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} data-id="element-243" />);
    }
    return <div className="flex" data-id="element-244">{stars}</div>;
  };
  // Funzione per renderizzare l'indice di affidabilità con colore
  const renderAffidabilita = (indice: number) => {
    let bgColor = 'bg-red-100 text-red-800';
    if (indice >= 80) {
      bgColor = 'bg-green-100 text-green-800';
    } else if (indice >= 60) {
      bgColor = 'bg-yellow-100 text-yellow-800';
    }
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor}`} data-id="element-245">
        {indice}%
      </span>;
  };
  // Funzione per renderizzare lo stato di sincronizzazione
  const renderSincronizzazione = (sincronizzato: boolean) => {
    return sincronizzato ? <span className="inline-flex items-center text-green-600" data-id="element-246">
        <CheckCircleIcon className="h-4 w-4 mr-1" data-id="element-247" />
        <span className="text-xs" data-id="element-248">Sincronizzato</span>
      </span> : <span className="inline-flex items-center text-red-600" data-id="element-249">
        <XCircleIcon className="h-4 w-4 mr-1" data-id="element-250" />
        <span className="text-xs" data-id="element-251">Non sincronizzato</span>
      </span>;
  };
  const [showReportConfirmation, setShowReportConfirmation] = useState<string | null>(null);
  const handleRequestReport = (clientId: string) => {
    setShowReportConfirmation(clientId);
  };
  const confirmRequestReport = (clientId: string) => {
    // Qui andresti a richiedere il report e a decrementare i crediti
    setShowReportConfirmation(null);
    // Mostra una notifica di conferma
    alert('Report richiesto con successo');
  };
  return <div className="overflow-x-auto" data-id="element-252">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-253">
        <thead className="bg-gray-50" data-id="element-254">
          <tr data-id="element-255">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-256">
              Ragione Sociale
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-257">
              P.IVA
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-258">
              Rating
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-259">
              Affidabilità
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-260">
              Importi Scaduti
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-261">
              Pagamenti Scaduti
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-262">
              Settore
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-263">
              Ultimo Pagamento
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-264">
              Sincronizzato
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-265">
              Report
            </th>
            <th scope="col" className="relative px-6 py-3" data-id="element-266">
              <span className="sr-only" data-id="element-267">Azioni</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-268">
          {filteredClients.length === 0 ? <tr data-id="element-269">
              <td colSpan={11} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-270">
                Nessun cliente trovato
              </td>
            </tr> : filteredClients.map(client => <tr key={client.id} className="hover:bg-gray-50 cursor-pointer" data-id="element-271">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-272">
                  <div className="text-sm font-medium text-gray-900" data-id="element-273">
                    {client.ragioneSociale}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-274">
                  <div className="text-sm text-gray-500" data-id="element-275">{client.piva}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-276">
                  {renderRating(client.rating)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-277">
                  {renderAffidabilita(client.indiceAffidabilita)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-278">
                  <div className={`text-sm font-medium ${client.importiScaduti === '€0' ? 'text-green-600' : 'text-red-600'}`} data-id="element-279">
                    {client.importiScaduti}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-280">
                  <div className={`text-sm font-medium ${client.pagamentiScaduti === 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-281">
                    {client.pagamentiScaduti}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-282">
                  <div className="text-sm text-gray-500" data-id="element-283">{client.settore}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-284">
                  <div className="text-sm text-gray-500" data-id="element-285">
                    {client.ultimoPagamento}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-286">
                  {renderSincronizzazione(client.sincronizzato)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-287">
                  {showReportConfirmation === client.id ? <div className="flex items-center space-x-2" data-id="element-288">
                      <div className="flex items-center text-amber-600" data-id="element-289">
                        <CoinsIcon className="h-4 w-4 mr-1" data-id="element-290" />
                        <span data-id="element-291">2 crediti</span>
                      </div>
                      <button onClick={() => confirmRequestReport(client.id)} className="bg-[#1A3E5C] text-white px-2 py-1 rounded text-xs" data-id="element-292">
                        Conferma
                      </button>
                      <button onClick={() => setShowReportConfirmation(null)} className="bg-gray-200 text-gray-800 px-2 py-1 rounded text-xs" data-id="element-293">
                        Annulla
                      </button>
                    </div> : <button onClick={e => {
              e.stopPropagation();
              handleRequestReport(client.id);
            }} className="inline-flex items-center text-sm text-blue-600 hover:text-blue-900" data-id="element-294">
                      <FileTextIcon className="h-4 w-4 mr-1" data-id="element-295" />
                      <span data-id="element-296">Report</span>
                    </button>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-297">
                  <ChevronRightIcon className="h-5 w-5 text-gray-400" data-id="element-298" />
                </td>
              </tr>)}
        </tbody>
      </table>
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6" data-id="element-299">
        <div className="flex-1 flex justify-between sm:hidden" data-id="element-300">
          <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-301">
            Precedente
          </button>
          <button className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-302">
            Successivo
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-303">
          <div data-id="element-304">
            <p className="text-sm text-gray-700" data-id="element-305">
              Visualizzazione{' '}
              <span className="font-medium" data-id="element-306">{filteredClients.length}</span> di{' '}
              <span className="font-medium" data-id="element-307">{clients.length}</span> clienti
            </p>
          </div>
          <div data-id="element-308">
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-309">
              <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-310">
                <span className="sr-only" data-id="element-311">Precedente</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-312">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" data-id="element-313" />
                </svg>
              </button>
              <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-314">
                1
              </button>
              <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-315">
                <span className="sr-only" data-id="element-316">Successivo</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-317">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" data-id="element-318" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>;
}